import { Music } from "lucide-react";
import { t } from "@workspace/strings/web";
import { nowPlayingConfig } from "@/player/lib/widgets";

/** What the player passes down about its own on-air audio. */
export interface ZoneNowPlayingInfo {
  title: string;
  artist: string | null;
  artworkUrl: string | null;
  playlistName: string | null;
}

export interface ZoneNowPlaying {
  current: ZoneNowPlayingInfo | null;
  upNext: ZoneNowPlayingInfo | null;
  playing: boolean;
}

/**
 * Fullscreen "now playing" board fed by the player's own playback state — the
 * widget renders inside the same shell that plays the audio, so no network or
 * realtime hop is involved.
 */
export function NowPlayingWidget({
  config,
  nowPlaying,
}: {
  config: Record<string, unknown> | null;
  nowPlaying: ZoneNowPlaying | null;
}) {
  const cfg = nowPlayingConfig(config);
  const current = nowPlaying?.current ?? null;

  if (!current) {
    return (
      <div
        className="flex h-full w-full flex-col items-center justify-center gap-[3vh] bg-background text-muted-foreground"
        data-testid="widget-nowplaying"
      >
        <Music className="size-[8vw]" aria-hidden="true" />
        <div className="text-[3vw]">{cfg.idleText}</div>
      </div>
    );
  }

  const upNext = cfg.showUpNext ? (nowPlaying?.upNext ?? null) : null;
  const showArt = cfg.showArtwork && Boolean(current.artworkUrl);

  return (
    <div
      className="flex h-full w-full items-center justify-center gap-[5vw] bg-background px-[6vw]"
      data-testid="widget-nowplaying"
    >
      {showArt && (
        <img
          src={current.artworkUrl ?? undefined}
          alt=""
          className="size-[34vh] shrink-0 rounded-2xl object-cover shadow-2xl"
          onError={(e) => {
            // Broken artwork degrades to text-only rather than a broken glyph.
            e.currentTarget.style.display = "none";
          }}
        />
      )}
      <div className="min-w-0 text-left">
        <div className="mb-[1.5vh] flex items-center gap-[1vw] text-[1.8vw] uppercase tracking-widest text-muted-foreground">
          <span
            className={
              nowPlaying?.playing
                ? "size-[0.9vw] rounded-full bg-emerald-500 animate-pulse"
                : "size-[0.9vw] rounded-full bg-muted-foreground/50"
            }
          />
          {t("player.widgets.nowPlaying.label")}
        </div>
        <div className="truncate text-[5vw] font-semibold leading-tight text-foreground">
          {current.title}
        </div>
        {current.artist && (
          <div className="mt-[1vh] truncate text-[3vw] text-muted-foreground">
            {current.artist}
          </div>
        )}
        {current.playlistName && (
          <div className="mt-[0.5vh] truncate text-[1.8vw] text-muted-foreground/70">
            {current.playlistName}
          </div>
        )}
        {upNext && (
          <div className="mt-[4vh] truncate text-[2vw] text-muted-foreground">
            {t("player.widgets.nowPlaying.upNext")}
            {": "}
            <span className="text-foreground/80">{upNext.title}</span>
            {upNext.artist ? ` — ${upNext.artist}` : ""}
          </div>
        )}
      </div>
    </div>
  );
}
